<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('Customers By Gender') }}</b-card-title>
      <feather-icon
        icon="RotateCwIcon"
        class="cursor-pointer"
        @click="fetchCustomersByGender"
      />
    </b-card-header>

    <vue-apex-charts
      type="pie"
      height="325"
      class="mt-2 mb-1"
      :options="pieChartOptions.chartOptions"
      :series="pieChartOptions.series"
    />

    <!-- list group -->
    <div class="p-5">
      <div
        v-for="(item, index) in listData"
        :key="index"
        class="d-flex justify-content-between"
        :class="index === Object.keys(listData).length - 1 ? '' : 'mb-1'"
      >
        <div class="series-info">
          <feather-icon
            :icon="item.icon"
            size="16"
            class="mr-50"
            :class="item.iconColor"
          />
          <span class="font-weight-bolder">{{ item.text }}</span>
        </div>
        <span>{{ item.result }}</span>
      </div>
    </div>

    <Spinner :show="isLoadingCustomerByGender" />
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { onMounted, ref, computed } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import Spinner from '@/views/components/spinner/Spinner.vue'
import useToastAlert from '../../../composable/useToast'
import toNumber from '@/helpers/toNumber'
import { getIconColor, getChartColor } from '@/helpers/getColors'

export default {
  components: {
    VueApexCharts,
    BCard,
    BCardHeader,
    BCardTitle,
    Spinner,
  },
  setup() {
    const { getCustomersByGender } = useActions({
      getCustomersByGender: 'dashboard/getCustomersByGender',
    })
    const { t } = useI18nUtils()
    const { toastAlert } = useToastAlert()
    const customers = ref([])
    const rawLabels = ref([])
    const isLoadingCustomerByGender = ref(false)

    const listData = computed(() => customers.value.map((item, index) => {
      const label = rawLabels.value[index] || 'Others'
      return {
        icon: 'StopCircleIcon',
        iconColor: getIconColor(label),
        text: label === '' ? t('Others') : t(label),
        result: toNumber(item),
      }
    // eslint-disable-next-line no-nested-ternary
    }).sort((a, b) => (a.text === b.text ? 0 : a.text < b.text ? -1 : 1)))

    const pieChartOptions = computed(() => ({
      series: customers.value,
      chartOptions: {
        chart: {
          toolbar: {
            show: false,
          },
        },
        labels: rawLabels.value.map(label => (label === '' ? t('Others') : t(label))),
        dataLabels: {
          enabled: true,
        },
        legend: { show: false },
        stroke: {
          width: 4,
        },
        colors: rawLabels.value.map(label => getChartColor(label !== '' ? label : 'Others')),
      },
    }))

    const fetchCustomersByGender = async () => {
      try {
        isLoadingCustomerByGender.value = true
        const response = await getCustomersByGender()

        const { status, data } = response
        if (status === 200 && data) {
          customers.value = data.map(item => item.totalCount)
          rawLabels.value = data.map(item => item.label || 'Others')
        }
      } catch (err) {
        toastAlert(err)
      } finally {
        isLoadingCustomerByGender.value = false
      }
    }

    onMounted(fetchCustomersByGender)

    return {
      isLoadingCustomerByGender,
      listData,
      pieChartOptions,
      fetchCustomersByGender,
    }
  },
}
</script>
